import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  Stack,
  Button,
  Container,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  MenuItem,
} from '@mui/material';
import { collection, doc, getDoc, setDoc, updateDoc, getFirestore, serverTimestamp } from 'firebase/firestore';
import axios from 'axios';
import firebaseApp from '../utils/firebaseConfig';

const db = getFirestore(firebaseApp);
const FUNCTIONS_BASE = 'https://us-central1-bb-target-center.cloudfunctions.net';

export default function NewMembershipPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    priceCents: 0,
    benefits: '',
    active: true,
    sortOrder: 0,
    intervalMonths: 12,
    stripePriceId: '',
    stripeProductId: '',
    // Precio especial para miembros activos que renuevan (0 = no aplica).
    // Aplica mientras la membresía esté vigente y hasta renewalGraceDays
    // días después de vencida.
    renewalPriceCents: 0,
    stripeRenewalPriceId: '',
    renewalGraceDays: 15,
    // Tarifas especiales (menores, policías/agentes): la compra online NO se
    // cobra de una — el cliente sube evidencia y queda pendiente de aprobación
    requiresEvidence: false,
    evidenceLabel: '',
  });
  const [priceDisplay, setPriceDisplay] = useState('');
  const [renewalPriceDisplay, setRenewalPriceDisplay] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (editId) loadMembership();
  }, [editId]);

  const loadMembership = async () => {
    try {
      const docSnap = await getDoc(doc(db, 'membership_plans', editId));
      if (docSnap.exists()) {
        const data = docSnap.data();
        setFormData({ ...formData, ...data });
        setPriceDisplay(((data.priceCents || 0) / 100).toFixed(2));
        setRenewalPriceDisplay(data.renewalPriceCents > 0 ? ((data.renewalPriceCents || 0) / 100).toFixed(2) : '');
      }
    } catch (err) {
      console.error('Error loading membership:', err);
    }
  };

  const handlePriceChange = (e) => {
    const val = e.target.value;
    setPriceDisplay(val);
    const cents = Math.round(parseFloat(val || '0') * 100);
    setFormData({ ...formData, priceCents: cents });
  };

  const handleRenewalPriceChange = (e) => {
    const val = e.target.value;
    setRenewalPriceDisplay(val);
    const cents = Math.round(parseFloat(val || '0') * 100);
    setFormData({ ...formData, renewalPriceCents: cents > 0 ? cents : 0 });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) { setError('El nombre es requerido'); return; }
    if (formData.priceCents <= 0) { setError('El precio debe ser mayor a $0'); return; }
    if (formData.renewalPriceCents > 0 && formData.renewalPriceCents >= formData.priceCents) {
      setError('El precio de renovación debe ser menor que el precio regular');
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const renewalGraceDays = Number(formData.renewalGraceDays) > 0 ? Number(formData.renewalGraceDays) : 15;
      if (editId) {
        // Cambiar un precio en Firestore no cambia lo que Stripe cobra: hay
        // que emitir un Price nuevo sobre el mismo Product y guardar su id.
        const snap = await getDoc(doc(db, 'membership_plans', editId));
        const saved = snap.exists() ? snap.data() : {};
        let stripePriceId = saved.stripePriceId || '';
        let stripeRenewalPriceId = saved.stripeRenewalPriceId || '';

        if (saved.stripeProductId && formData.priceCents !== saved.priceCents) {
          const priceRes = await axios.post(`${FUNCTIONS_BASE}/createMembershipPrice`, {
            productId: saved.stripeProductId,
            priceCents: formData.priceCents,
            intervalMonths: formData.intervalMonths,
          });
          stripePriceId = priceRes.data.priceId;
        }

        if (formData.renewalPriceCents > 0) {
          if (saved.stripeProductId && (formData.renewalPriceCents !== saved.renewalPriceCents || !stripeRenewalPriceId)) {
            const renewRes = await axios.post(`${FUNCTIONS_BASE}/createMembershipPrice`, {
              productId: saved.stripeProductId,
              priceCents: formData.renewalPriceCents,
              intervalMonths: formData.intervalMonths,
              isRenewal: true,
            });
            stripeRenewalPriceId = renewRes.data.priceId;
          }
        } else {
          stripeRenewalPriceId = '';
        }

        await updateDoc(doc(db, 'membership_plans', editId), {
          name: formData.name.trim(),
          description: formData.description.trim(),
          priceCents: formData.priceCents,
          benefits: formData.benefits.trim(),
          active: formData.active,
          sortOrder: formData.sortOrder,
          intervalMonths: formData.intervalMonths,
          requiresEvidence: formData.requiresEvidence,
          evidenceLabel: formData.evidenceLabel.trim(),
          renewalPriceCents: formData.renewalPriceCents > 0 ? formData.renewalPriceCents : 0,
          renewalGraceDays,
          stripePriceId,
          stripeRenewalPriceId,
          updatedAt: serverTimestamp(),
        });
        setSuccess('Membresía actualizada');
      } else {
        const stripeRes = await axios.post(`${FUNCTIONS_BASE}/createMembershipProduct`, {
          name: formData.name.trim(),
          description: formData.description.trim(),
          priceCents: formData.priceCents,
          intervalMonths: formData.intervalMonths,
          renewalPriceCents: formData.renewalPriceCents > 0 ? formData.renewalPriceCents : 0,
        });
        const { productId, priceId, renewalPriceId } = stripeRes.data;

        const newRef = doc(collection(db, 'membership_plans'));
        await setDoc(newRef, {
          name: formData.name.trim(),
          description: formData.description.trim(),
          priceCents: formData.priceCents,
          benefits: formData.benefits.trim(),
          active: formData.active,
          sortOrder: formData.sortOrder,
          requiresEvidence: formData.requiresEvidence,
          evidenceLabel: formData.evidenceLabel.trim(),
          stripeProductId: productId,
          stripePriceId: priceId,
          renewalPriceCents: formData.renewalPriceCents > 0 ? formData.renewalPriceCents : 0,
          stripeRenewalPriceId: renewalPriceId || '',
          renewalGraceDays,
          subscriberCount: 0,
          intervalMonths: formData.intervalMonths,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        setSuccess('Membresía creada exitosamente');
      }
      setTimeout(() => navigate('/dashboard/memberships'), 1000);
    } catch (err) {
      console.error('Error saving membership:', err);
      setError(err.response?.data?.error || 'Error guardando la membresía');
    }
    setLoading(false);
  };

  const formatPrice = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

  const INTERVAL_OPTIONS = [
    { value: 6, label: '6 meses' },
    { value: 12, label: '1 año' },
    { value: 24, label: '2 años' },
    { value: 36, label: '3 años' },
    { value: 48, label: '4 años' },
    { value: 60, label: '5 años' },
  ];

  const intervalLabel = (months) => {
    const opt = INTERVAL_OPTIONS.find((o) => o.value === months);
    return opt ? opt.label : `${months} meses`;
  };

  return (
    <>
      <Helmet>
        <title>{editId ? 'Editar' : 'Nueva'} Membresía | BBTC Dashboard</title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>{editId ? 'Editar' : 'Nueva'} Membresía</Typography>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Información del Plan</Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nombre del plan"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="ej. Membresía Premium"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Descripción"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    multiline
                    rows={3}
                    placeholder="Descripción del plan de membresía"
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Precio ($)"
                    type="number"
                    value={priceDisplay}
                    onChange={handlePriceChange}
                    InputProps={{ startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>$</Typography> }}
                    inputProps={{ min: 0, step: '0.01' }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    select
                    label="Renovación cada"
                    value={formData.intervalMonths}
                    onChange={(e) => setFormData({ ...formData, intervalMonths: Number(e.target.value) })}
                  >
                    {INTERVAL_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Orden"
                    value={formData.sortOrder}
                    onChange={(e) => setFormData({ ...formData, sortOrder: Number(e.target.value) || 0 })}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Precio de renovación (miembros activos)"
                    type="number"
                    value={renewalPriceDisplay}
                    onChange={handleRenewalPriceChange}
                    InputProps={{ startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>$</Typography> }}
                    inputProps={{ min: 0, step: '0.01' }}
                    helperText="Déjalo vacío si no aplica"
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Días de gracia tras vencer"
                    value={formData.renewalGraceDays}
                    onChange={(e) => setFormData({ ...formData, renewalGraceDays: Number(e.target.value) || 0 })}
                    inputProps={{ min: 0 }}
                    helperText="Días después del vencimiento en que aún aplica el precio de renovación"
                    disabled={!(formData.renewalPriceCents > 0)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    El precio de renovación aplica automáticamente en la tienda a miembros con membresía vigente
                    o vencida hace menos de los días de gracia. Los demás pagan el precio regular.
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Beneficios"
                    value={formData.benefits}
                    onChange={(e) => setFormData({ ...formData, benefits: e.target.value })}
                    multiline
                    rows={4}
                    placeholder="Un beneficio por línea:&#10;Descuento en lanes&#10;Acceso a eventos exclusivos&#10;Precio preferencial en cursos"
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Switch checked={formData.requiresEvidence || false} onChange={(e) => setFormData({ ...formData, requiresEvidence: e.target.checked })} />}
                    label="Requiere evidencia (aprobación manual)"
                  />
                  <Typography variant="caption" color="text.secondary" display="block">
                    Para tarifas especiales (menores, policías, agentes). En la tienda online el cliente sube su evidencia,
                    guarda la tarjeta SIN cobro, y la compra queda pendiente hasta que se apruebe en el dashboard.
                    Al aprobar, se cobra la tarjeta y se activa la membresía.
                  </Typography>
                </Grid>

                {formData.requiresEvidence && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Qué evidencia pedir"
                      value={formData.evidenceLabel || ''}
                      onChange={(e) => setFormData({ ...formData, evidenceLabel: e.target.value })}
                      placeholder="ej. Foto de ID que muestre la edad, placa o credencial de agencia"
                      helperText="Este texto se le muestra al cliente en la tienda al pedirle la evidencia"
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Switch checked={formData.active} onChange={(e) => setFormData({ ...formData, active: e.target.checked })} />}
                    label="Activo"
                  />
                </Grid>
              </Grid>

              <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                <Button variant="contained" size="large" onClick={handleSubmit} disabled={loading}>
                  {loading ? <CircularProgress size={24} color="inherit" /> : (editId ? 'Actualizar' : 'Crear Membresía')}
                </Button>
                <Button variant="outlined" size="large" onClick={() => navigate('/dashboard/memberships')}>
                  Cancelar
                </Button>
              </Stack>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ p: 3, position: 'sticky', top: 20 }}>
              <Typography variant="h6" gutterBottom>Vista Previa</Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="h5" fontWeight="bold">{formData.name || 'Nombre del plan'}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                {formData.description || 'Sin descripción'}
              </Typography>
              <Typography variant="h4" color="primary" fontWeight="bold">
                {formatPrice(formData.priceCents)}
                <Typography component="span" variant="body2" color="text.secondary">
                  /{intervalLabel(formData.intervalMonths)}
                </Typography>
              </Typography>
              {formData.renewalPriceCents > 0 && (
                <Typography variant="body2" color="success.main" sx={{ mt: 0.5 }}>
                  Renovación miembros activos: <strong>{formatPrice(formData.renewalPriceCents)}</strong>
                  {' '}(hasta {formData.renewalGraceDays || 15} días después de vencer)
                </Typography>
              )}
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>Renovación</Typography>
              <Chip label={`Cada ${intervalLabel(formData.intervalMonths)}`} size="small" color="info" sx={{ mb: 2 }} />
              {formData.requiresEvidence && (
                <Chip label="Requiere evidencia y aprobación" size="small" color="warning" sx={{ mb: 2, ml: 1 }} />
              )}
              {formData.benefits && (
                <>
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>Beneficios</Typography>
                  <Stack spacing={0.5}>
                    {formData.benefits.split('\n').filter(Boolean).map((b, i) => (
                      <Typography key={i} variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span style={{ color: '#28a745' }}>✓</span> {b.trim()}
                      </Typography>
                    ))}
                  </Stack>
                </>
              )}

              {editId && formData.stripeProductId && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="caption" color="text.secondary">
                    Stripe Product: {formData.stripeProductId}
                  </Typography>
                  <br />
                  <Typography variant="caption" color="text.secondary">
                    Stripe Price: {formData.stripePriceId}
                  </Typography>
                </>
              )}
            </Card>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}
